import * as React from 'react'
import styled, { css } from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import * as rangeUtils from './range'

export type SliderChangeEvent = { value: rangeUtils.Value; dispatch: boolean }

type SliderInputProps = {
  className?: string
  boundary: rangeUtils.Boundary
  value: rangeUtils.Value
  onChange: (event: SliderChangeEvent) => void
}

function SliderInputComponent({ className, boundary, value, onChange }: SliderInputProps): React.ReactElement {
  const roundBoundary: rangeUtils.Boundary = {
    min: Math.floor(boundary.min),
    max: Math.ceil(boundary.max),
  }
  const sanitizedValue = rangeUtils.sanitize(value, boundary)
  const percentRange = rangeUtils.getPercentOfBoundary(sanitizedValue, roundBoundary)

  return (
    <div className={className}>
      <Thumb
        type="range"
        step="1"
        min={roundBoundary.min}
        max={roundBoundary.max}
        value={sanitizedValue.start}
        onTouchEnd={handleRangeChange({ dispatch: true, type: 'start' })}
        onMouseUp={handleRangeChange({ dispatch: true, type: 'start' })}
        onInput={handleRangeChange({ dispatch: false, type: 'start' })}
        $higher={sanitizedValue.start > roundBoundary.max - 100}
      />
      <Thumb
        type="range"
        step="1"
        min={roundBoundary.min}
        max={roundBoundary.max}
        value={sanitizedValue.end}
        onTouchEnd={handleRangeChange({ dispatch: true, type: 'end' })}
        onMouseUp={handleRangeChange({ dispatch: true, type: 'end' })}
        onInput={handleRangeChange({ dispatch: false, type: 'end' })}
      />

      <Slider>
        <Track />
        <Range style={{ left: `${percentRange.start}%`, width: `${percentRange.end - percentRange.start}%` }} />
      </Slider>
    </div>
  )

  function handleRangeChange(config: { dispatch: boolean; type: keyof rangeUtils.Value }) {
    return (event: React.UIEvent<HTMLInputElement>) => {
      const nextValue = { ...sanitizedValue, [config.type]: Number(event.currentTarget.value) }

      onChange({ value: rangeUtils.sanitize(nextValue, boundary), dispatch: config.dispatch })
    }
  }
}

export const SliderInput = styled(SliderInputComponent)`
  position: relative;
  width: 100%;
`

const Track = styled.div`
  position: absolute;
  border-radius: 1px;
  height: 3px;
  background-color: ${plume.COLORS.PRIMARY_SAND};
  width: 100%;
  z-index: 1;
`

const Range = styled.div`
  position: absolute;
  border-radius: 1px;
  height: 3px;
  background-color: ${plume.COLORS.PRIMARY_BLUE};
  z-index: 2;
`

const Slider = styled.div`
  position: relative;
  width: 100%;
`

const Thumb = styled.input<{ $higher?: boolean }>`
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;

  pointer-events: none;
  position: absolute;
  height: 0;
  width: 100%;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;

    box-sizing: content-box;
    background-color: ${plume.COLORS.PRIMARY_SAND};
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 3px 4px 1px #00000020;
    cursor: pointer;
    height: 18px;
    width: 18px;
    margin-top: 4px;
    pointer-events: all;
    position: relative;
  }

  &::-moz-range-thumb {
    box-sizing: content-box;
    background-color: ${plume.COLORS.PRIMARY_SAND};
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 3px 4px 1px #00000020;
    cursor: pointer;
    height: 18px;
    width: 18px;
    margin-top: 4px;
    pointer-events: all;
    position: relative;
  }

  &:first-of-type {
    z-index: 3;
  }

  &:last-of-type {
    z-index: 4;
  }

  ${({ $higher }) => {
    if ($higher) {
      return css`
        z-index: 5;
      `
    }
  }}
`
